import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Stack, Typography, Button, Alert, Checkbox, Link } from '@mui/material';
import axiosInstance from '../utils/axiosInstance';
import TableView from '../components/TableView';
import { getFields, searchFields } from '../utils/fields';
import Pagination from '../components/Pagination';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { formatDate, formatCurrency } from '../utils/format';
import SearchForm from '../components/SearchForm';
import RelatedMenu from '../components/RelatedMenu';
import { useSelectRow } from '../utils/useSelectRow';
import useDataFetch from '../hooks/useDataFetch';
import BillingClosingSection from '../components/BillingClosingSection';
const BillingCreatePage = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { selectedItems, setSelectedItems, handleRowSelect, handleSelectAll } = useSelectRow('order_id');
    const {
        dataset,
        currentPage,
        totalPages,
        searchQuery,
        handleSearchChange,
        handleSearchSubmit,
        handleClearSearch,
        handlePageChange,
        refetch
    } = useDataFetch('/invoice/immediate-preview', {
        client_name: '',
        order_numbers: '',
        dates: '',
    });
    const fieldsToInclude = [
        'dates',
        'order_numbers',
        'client_name'
    ];

    const searchItems = getFields(
        searchFields,
        fieldsToInclude
    );
    const handleCreateInvoice = async (row) => {
        const confirmed = window.confirm(
            `${row.client_name} の注文で請求作成しますか？`
        );
        if (!confirmed) return;
        try {
            await axiosInstance.post('/invoice/immediate-create', {
                order_id: row.order_id
            });
            showSnackbar('請求書を作成しました', 'success');
            refetch();
        } catch (err) {
            console.error(err);
            showSnackbar(
                err?.response?.data?.message || '作成に失敗しました',
                'error'
            );
        }
    };
    const handleCreateSelected = async () => {

        if (selectedItems.length === 0) {
            showSnackbar('選択されていません', 'warning');
            return;
        }

        const confirmed = window.confirm(
            `${selectedItems.length}件の注文を請求書化しますか？`
        );

        if (!confirmed) return;

        try {
            await Promise.all(
                selectedItems.map((orderId) =>
                    axiosInstance.post('/invoice/immediate-create', {
                        order_id: orderId
                    })
                )
            );

            showSnackbar('請求書を一括作成しました', 'success');

            setSelectedItems([]);
            refetch();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '一括作成に失敗しました',
                'error'
            );
        }
    };
    const handleCreateGrouped = async () => {
        if (selectedItems.length === 0) {
            showSnackbar('選択されていません', 'warning');
            return;
        }

        const confirmed = window.confirm(
            `${selectedItems.length}件の注文を1つの請求書にまとめますか？`
        );

        if (!confirmed) return;

        try {
            await axiosInstance.post('/invoice/group-create', {
                order_ids: selectedItems
            });

            showSnackbar('まとめ請求書を作成しました', 'success');
            setSelectedItems([]);
            refetch();

        } catch (err) {
            showSnackbar(
                err?.response?.data?.message || '作成に失敗しました',
                'error'
            );
        }
    };
    const handleRowClick = async (item) => {
        navigate(`/orders/${item.order_id}`);
    };
    const columns = [
        {
            label: (
                <Checkbox
                    checked={selectedItems.length === dataset.length}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < dataset.length}
                    onChange={(e) => handleSelectAll(e, dataset)}
                />
            ),
            accessor: 'checkbox',
            render: (item) => (
                <Checkbox
                    checked={selectedItems.includes(item.order_id)}
                    onChange={() => handleRowSelect(item.order_id)}
                />
            ),
            width: '50px',
        },
        {
            label: '注文日',
            accessor: 'order_date',
            width: '160px',
            render: (item) => `${formatDate(item.order_date)}`
        },
        {
            label: '注文番号',
            accessor: 'order_number',
            width: '160px',
            render: (item) => (
                <Link
                    component="button"
                    onClick={() => handleRowClick(item)}>
                    {item.order_number}
                </Link>
            ),
        },
        {
            label: '取引先',
            accessor: 'client_name',
            width: '320px',
            render: (item) => <Typography>{item.client_name} </Typography>
        },
        {
            label: '金額',
            accessor: 'total_amount',
            width: '160px',
            render: (item) => (
                <>
                    {formatCurrency(item.total_amount)}<br />
                    <Typography variant="caption">税込 {formatCurrency(item.total_amount_including_tax)}</Typography>
                </>
            ),
        },
        {
            label: '',
            accessor: 'action',
            width: '60px',
            render: (item) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCreateInvoice(item)}
                >
                    作成
                </Button>
            )
        }
    ];

    return (
        <>
            <Typography variant="h1" component="h1">請求作成</Typography>
            <Paper variant="section">
                <Typography variant='h4'>締め対象</Typography>
                <BillingClosingSection />
            </Paper>
            <Paper variant="section">
                <Typography variant='h4'>請求未作成</Typography>
                <Alert severity="info" sx={{ mb: 1 }}>請求書が作成されていない注文が表示されています。</Alert>
                {/* 検索フォーム */}
                <SearchForm
                    searchQuery={searchQuery}
                    searchFields={searchItems}
                    handleSearchChange={handleSearchChange}
                    handleSearchSubmit={handleSearchSubmit}
                    handleClearSearch={handleClearSearch}
                />
                <Button
                    variant="contained"
                    disabled={selectedItems.length === 0}
                    onClick={handleCreateSelected}
                    sx={{ my: 1, mr: 1 }}
                >
                    選択した注文で個別に請求作成
                </Button>
                <Button
                    variant="outlined"
                    disabled={selectedItems.length === 0}
                    onClick={handleCreateGrouped}
                    sx={{ my: 1 }}
                >
                    選択した注文で請求を1つにまとめる
                </Button>
                <TableView
                    dataset={dataset}
                    columns={columns}
                    keyColumn="order_id"
                />
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </Paper>
        </>
    );
};

export default BillingCreatePage;